import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Schema } from '../../../../../amplify/data/resource';
import { FundLogo } from '../../../../components/custom/FundLogo';
import StatusTextCircle from '../../../../components/custom/StatusTextCircle';
import ArrowIcon from '../../../../components/icons/ArrowIcon';
import { formatMoney } from '../../../../utils/format/format';
import Sidebar from '../../AgentDashboard/PaymentList/Sidebar';

const paymentSelectionSet = [
  'id',
  'link',
  'validUntil',
  'createdAt',
  'sentBy',
  'clientBank',
  'clientBranch',
  'clientAccount',
  'clientFullName',
  'clientId',
  'fee',
  'paymentAmount',
  'polisaNumber',
  'description',
  'product',
  'user.fullName',
  'user.agencyName',
];

// @ts-ignore
type Payment = SelectionSet<
  // @ts-ignore
  Schema['AgentPaymentLink']['type'],
  typeof paymentSelectionSet
>;

interface FileItemProps {
  payment: Payment;
}

const apiClient = generateClient<Schema>();

const PaymentItem: React.FC<FileItemProps> = ({ payment }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [openDetails, setOpenDetails] = useState(false);
  const [openLink, setOpenLink] = useState(false);
  const [isLinkValid, setIsLinkValid] = useState(false);

  useEffect(() => {
    const checkLinkValidity = () => {
      if (!payment.validUntil || !payment.link) {
        setIsLinkValid(false);
        return;
      }

      const validUntil = new Date(payment.validUntil).getTime();
      const now = new Date().getTime();

      setIsLinkValid(validUntil > now);
    };

    checkLinkValidity();

    const intervalId = setInterval(checkLinkValidity, 10000);

    return () => clearInterval(intervalId);
  }, [payment]);

  const handleCopyClick = () => {
    if (!payment.link || !isLinkValid) return;

    navigator.clipboard
      .writeText(payment.link)
      .then(() => {
        enqueueSnackbar('הקישור הועתק בהצלחה!', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('אירעה שגיאה בהעתקת הקישור.', { variant: 'error' });
        console.error('Failed to copy link: ', err);
      });
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: '#F2F4F7',
          borderRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          verticalAlign: 'bottom',
        }}
      >
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '2px',
            }}
          >
            <Typography
              fontSize="16px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {format(new Date(payment.createdAt), 'MMMM', { locale: he })}
            </Typography>
            <Typography
              fontWeight={'400'}
              fontSize={'12px'}
              fontFamily={'Montserrat'}
            >
              {format(new Date(payment.createdAt), 'dd/MM/yyyy', {
                locale: he,
              })}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment.user.fullName}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment?.user?.agencyName}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment?.clientFullName}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment?.clientId}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <FundLogo width="100px" logoIndex={payment?.indexLogo || ''} />
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment?.product}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <img src={'/assets/dashboard/coins.svg'} alt="coins" />
            <Typography
              fontWeight={'500'}
              fontSize={'20px'}
              fontFamily={'Montserrat'}
            >
              {formatMoney(Number(payment?.paymentAmount))}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <StatusTextCircle status={payment.status || 'PENDING'} />
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {payment?.fee}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            py: '20px',
            borderBottom: 'none',
            textAlign: 'left',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={1}
            gap={1}
            sx={{ width: '100%' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '45px',
                  height: '45px',
                  background: 'white',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setOpenDetails(true);
                }}
              >
                <ArrowIcon />
              </Box>
            </Box>
          </Stack>
        </TableCell>
      </TableRow>

      <Sidebar
        open={openDetails}
        toggleSidebar={() => {
          setOpenDetails(!openDetails);
        }}
        paymentId={payment.id}
      />
    </>
  );
};

export default PaymentItem;
